import { normalizeWorkoutRepsMode } from './workoutRepsMode';
import { ensureSeriesPlan, isPyramidExercise, parseSeriesPlan } from './workoutSeriesPlan';
import { formatExerciseDisplay } from '../utils/exerciseFormatters';

/** Inferisce reps_mode da campi template (ripetizioni_min/max). */
export function inferTemplateRepsMode(exercise) {
    if (exercise?.reps_mode) {
        return normalizeWorkoutRepsMode(exercise.reps_mode);
    }
    const isCardio = exercise?.is_cardio === 1 || exercise?.is_cardio === true;
    const isPyramid = exercise?.set_type === 'piramidale';
    if (isCardio || isPyramid) return 'fixed';

    const min = Number(exercise?.ripetizioni_min ?? 0);
    const max = Number(exercise?.ripetizioni_max ?? 0);
    if (min === 0 && max === 0) return 'amrap';
    if (min > 0 && max > 0 && min !== max) return 'range';
    return 'fixed';
}

/** Payload ripetizioni_min/max per API template. */
export function buildTemplateRepsPayload(exercise, ripetizioniMaxFallback) {
    const isCardio = exercise.is_cardio === 1 || exercise.is_cardio === true;
    const mode = inferTemplateRepsMode(exercise);

    if (exercise.set_type === 'piramidale' && exercise.series_plan?.length) {
        return {
            ripetizioni_min: exercise.series_plan[0].reps,
            ripetizioni_max: exercise.series_plan[exercise.series_plan.length - 1].reps,
        };
    }

    if (mode === 'range') {
        return {
            ripetizioni_min: parseInt(exercise.ripetizioni_min, 10) || 8,
            ripetizioni_max: parseInt(exercise.ripetizioni_max, 10) || 12,
        };
    }

    if (mode === 'amrap') {
        return { ripetizioni_min: 0, ripetizioni_max: 0 };
    }

    const max = parseInt(ripetizioniMaxFallback ?? exercise.ripetizioni_max, 10) || (isCardio ? 20 : 12);
    return { ripetizioni_min: 0, ripetizioni_max: max };
}

/**
 * Sintesi piramidale per anteprima: «10 → 12 → 14» o «10@40kg → 12@50kg».
 */
export function formatTemplatePyramidTarget(exercise) {
    if (!isPyramidExercise(exercise)) return null;

    const plan = ensureSeriesPlan({
        set_type: 'piramidale',
        serie: exercise.serie,
        ripetizioni: exercise.ripetizioni_max ?? exercise.ripetizioni_min,
        peso: exercise.peso_suggerito,
        series_plan: parseSeriesPlan(exercise.series_plan),
    });

    if (!plan?.length) return null;

    const hasWeights = plan.some((r) => r.weight != null && Number(r.weight) > 0);
    if (hasWeights) {
        return plan
            .map((r) => {
                const reps = r.reps ?? '?';
                const w = r.weight != null && Number(r.weight) > 0 ? `@${Number(r.weight)}kg` : '';
                return `${reps}${w}`;
            })
            .join(' → ');
    }

    return plan.map((r) => r.reps).join(' → ');
}

/**
 * Target reps compatto per singolo esercizio template (senza serie).
 */
export function formatTemplateRepsTarget(exercise) {
    if (!exercise) return '';

    const isCardio = exercise.is_cardio === 1 || exercise.is_cardio === true;
    const isIsometric =
        exercise.is_isometric === 1 ||
        exercise.is_isometric === true ||
        exercise.is_isometric_scheda === 1 ||
        exercise.is_isometric_scheda === true;

    if (isPyramidExercise(exercise)) {
        const pyramid = formatTemplatePyramidTarget(exercise);
        if (pyramid) return pyramid;
    }

    const mode = inferTemplateRepsMode(exercise);

    if (mode === 'amrap' && !isCardio && !isIsometric) {
        return 'A cedimento';
    }

    if (mode === 'range' && !isCardio && !isIsometric) {
        const min = exercise.ripetizioni_min;
        const max = exercise.ripetizioni_max;
        if (min != null && max != null && min !== max) {
            return `${min}–${max} reps`;
        }
    }

    if (isCardio || isIsometric) {
        const max = exercise.ripetizioni_max ?? exercise.ripetizioni_min;
        return formatExerciseDisplay(max, exercise);
    }

    const max = exercise.ripetizioni_max ?? exercise.ripetizioni_min;
    return max != null ? `${max} reps` : '';
}

/** Riga completa anteprima: «3 × 10 → 12 → 14» / «3 × A cedimento». */
export function formatTemplateRepsLine(exercise) {
    const serie = exercise?.serie ?? 1;
    const target = formatTemplateRepsTarget(exercise);
    if (!target) return `${serie} × —`;
    return `${serie} × ${target}`;
}

/** Peso suggerito da mostrare in anteprima (nascosto se già nel piano piramidale). */
export function formatTemplateWeightHint(exercise) {
    if (!exercise) return null;
    if (isPyramidExercise(exercise)) {
        const plan = parseSeriesPlan(exercise.series_plan);
        if (plan.some((r) => r.weight != null && Number(r.weight) > 0)) {
            return null;
        }
    }
    const w = Number(exercise.peso_suggerito);
    return w > 0 ? `${w}kg` : null;
}
